import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { ArrowRight, EyeOff } from 'lucide-react';

interface ThStickyProps {
  children: React.ReactNode;
}

export function ThSticky({ children }: ThStickyProps) {
  return (
    <th className="border-primary/15 bg-background md:w-85 md:max-w-85 top-0 z-50 hidden w-64 max-w-64 rounded-t-xl border p-3 text-left sm:sticky sm:left-3 sm:table-cell md:left-6 md:rounded-t-2xl md:p-6">
      <div className="text-primary-dark flex items-center justify-between">
        <div className="text-base font-medium md:text-lg">{children}</div>
        <ArrowRight className="size-4 md:size-5" />
      </div>
    </th>
  );
}

interface ThProps {
  title: string;
  img?: string;
  className?: string;
  imgClassName?: string;
}

export function Th({ title, img, className, imgClassName }: ThProps) {
  return (
    <th
      className={cn(
        'border-primary/15 z-9 bg-background md:w-85 md:max-w-85 sticky top-0 h-full w-64 max-w-64 overflow-hidden rounded-t-xl border p-0 align-top md:rounded-t-2xl',
        className,
      )}
    >
      <div className="flex h-24 flex-col md:h-28">
        <div className="bg-primary/10 text-primary-dark py-1 text-center text-xs font-medium md:py-2">
          {title}
        </div>
        <div className="grid flex-1 place-items-center px-3 py-3 md:px-16 md:py-4">
          {img ? (
            <img
              src={img}
              alt={title}
              className={cn('mx-auto h-auto w-28 md:w-32', imgClassName)}
            />
          ) : (
            <div className="text-primary-dark text-balance py-1.5 text-lg font-semibold md:text-xl">
              {title}
            </div>
          )}
        </div>
      </div>
    </th>
  );
}

interface TdProps {
  title?: string;
  children?: React.ReactNode;
  isSticky?: boolean;
  header?: string;
  className?: string;
  onHide?: () => void;
}

export function Td({ title, children, isSticky, header, className, onHide }: TdProps) {
  return (
    <td
      className={cn(
        'border-primary/15 bg-background relative border-x border-b px-3 py-3 align-middle md:px-6 md:py-4',
        className,
        {
          'group/row z-10 sm:sticky sm:left-3 md:left-6': isSticky,
        },
      )}
    >
      <div
        className={cn('text-center', {
          'text-start': isSticky,
        })}
      >
        <div
          className={cn('text-primary-dark text-balance text-sm font-medium md:text-base', {
            'pt-5.5 sm:pt-0': header,
            'hidden sm:block': !title,
          })}
        >
          {title ?? <>&nbsp;</>}
        </div>
        {header && (
          <div className="absolute left-0 top-0 block w-full bg-gray-100 py-1 text-center text-xs font-medium text-gray-700 sm:hidden md:py-2">
            {header}
          </div>
        )}
        {children && (
          <div
            className={cn({
              'pt-5.5 sm:pt-0': header,
            })}
          >
            {children}
          </div>
        )}
      </div>
      {onHide && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onHide}
              className="text-muted-foreground hover:text-primary absolute right-2 top-2 opacity-0 transition-opacity group-hover/row:opacity-100 md:right-3 md:top-3"
              aria-label="Hide row"
            >
              <EyeOff className="size-3.5 md:size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Hide row</TooltipContent>
        </Tooltip>
      )}
    </td>
  );
}

interface TfProps {
  children: React.ReactNode;
  className?: string;
}

export function Tf({ children, className }: TfProps) {
  return (
    <td
      className={cn(
        'border-primary/15 bg-primary/10 rounded-b-xl border p-3 md:rounded-b-2xl md:p-6',
        className,
      )}
    >
      <div className="text-primary-dark space-y-2 text-center">{children}</div>
    </td>
  );
}
